import React, { useMemo } from 'react';
import { useWatch, Control } from 'react-hook-form';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';

import { PayoutsForm } from '@/modules/onboarding/model/payoutsStep';
import { GBPartnerType } from '@/modules/partner/model/partnerType';
import { useGBTaxProfile } from '@/modules/taxProfile/hooks/useGBTaxProfile';
import { MAIN_HOST } from '@/shared/utils/getSiteDomain';
import { CheckboxField } from '@src-common/view/adapters/reactHookForm/CheckboxField';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

type Props = {
  control: Control<PayoutsForm>;
};

const messages = defineMessages({
  phoTitle: {
    defaultMessage: 'Do you hold private hire operators license?',
    id: 'gb_pho_license_title',
  },
  phoDescription: {
    defaultMessage: 'If you do, please upload your license in the document field below.',
    id: 'gb_pho_license_desc',
  },
  phoRequired: {
    defaultMessage: 'Please answer the question',
    id: 'gb_pho_license_required',
  },
  yesValue: {
    defaultMessage: 'Yes',
    id: 'gb_pho_license_yes',
  },
  noValue: {
    defaultMessage: 'No',
    id: 'gb_pho_license_no',
  },
});

const GbPaymentType: React.FC<Props> = ({ control }) => {
  const intl = useIntl();

  const yesNoItems = useMemo(
    () => [
      {
        title: intl.formatMessage(messages.yesValue),
        value: 'yes',
      },
      {
        title: intl.formatMessage(messages.noValue),
        value: 'no',
      },
    ],
    [intl],
  );

  const partnerTypes = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: 'Pay myself',
          id: '6Sy04Z',
          description: 'Payout type option title - Pay myself',
        }),
        value: GBPartnerType.selfEmployed,
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Pay my company',
          id: 'Ar+x4C',
          description: 'Payout type option title - Pay my company',
        }),
        value: GBPartnerType.company,
      },
    ],
    [intl],
  );

  const selectedPartnerType = useWatch({ control, name: 'partnerType' });
  const selectedTaxProfile = useWatch({ control, name: 'taxProfile' });

  const { taxProfileOptions, vatValidationRules, vatDescription, isVatRequired } =
    useGBTaxProfile(selectedTaxProfile);
  const isCompany = selectedPartnerType === GBPartnerType.company;

  return (
    <>
      <SelectField
        control={control}
        name="partnerType"
        title={intl.formatMessage({
          defaultMessage: 'PAYOUT TYPE',
          id: 'MafFrv',
          description: 'PAYOUT TYPE field title',
        })}
        items={partnerTypes}
      />

      <SelectField
        control={control}
        name="hasPhoLicense"
        title={intl.formatMessage(messages.phoTitle)}
        description={intl.formatMessage(messages.phoDescription)}
        items={yesNoItems}
        rules={{
          validate: value =>
            value === 'yes' || value === 'no' ? true : intl.formatMessage(messages.phoRequired),
        }}
      />

      <SelectField
        control={control}
        name="taxProfile"
        title={intl.formatMessage({
          defaultMessage: 'Tax Profile',
          id: 'tax_profile_field_title',
          description: 'TAX PROFILE field title',
        })}
        items={taxProfileOptions}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Tax profile is required',
            id: 'error_tax_profile_required',
            description: 'Tax profile required error message',
          }),
        }}
      />

      {isVatRequired && (
        <TextField
          name="vatRegNo"
          control={control}
          title={intl.formatMessage({
            defaultMessage: 'VAT REG NO',
            id: '9u8nc2',
            description: 'VAT REG NO field title',
          })}
          rules={vatValidationRules}
          description={vatDescription}
        />
      )}

      <CheckboxField
        name="vatAgreementAccepted"
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'You must accept the Agreement to continue',
            id: 'vat_agreement_required',
            description: 'VAT agreement checkbox required error message',
          }),
        }}
      >
        <FormattedMessage
          defaultMessage="I confirm that information provided above is accurate. By saving these details, I agree to either: (a) the payment arrangements set out in clause 8 of the Partner Agreement with Wheely Technologies Ltd; or (b) to the Self-Billing arrangement set out in clause 4 of the <a>Sub-Partner Agreement</a> with Wheely London Ltd. In the latter case, I authorise Wheely London Ltd to issue VAT invoices on my behalf. I agree to notify Wheely immediately of any changes to my registration."
          id="vat_agreement_checkbox"
          description="VAT agreement checkbox text"
          values={{
            a: (text: React.ReactNode) => (
              <a
                href={`https://${MAIN_HOST}/en/legal/uk/for-drivers/sub-agreement`}
                target="_blank"
                rel="noreferrer"
              >
                {text}
              </a>
            ),
          }}
        />
      </CheckboxField>

      {isCompany && (
        <>
          <TextField
            shouldUnregister={true}
            name="companyName"
            control={control}
            title={intl.formatMessage({
              defaultMessage: 'COMPANY NAME',
              id: 'w8qpaR',
              description: 'COMPANY NAME field title',
            })}
          />

          <TextField
            shouldUnregister={true}
            name="companyAddress"
            control={control}
            title={intl.formatMessage({
              defaultMessage: 'COMPANY ADDRESS',
              id: 'jfwv8e',
              description: 'COMPANY ADDRESS field title',
            })}
          />

          <TextField
            shouldUnregister={true}
            name="companyRegNumber"
            control={control}
            title={intl.formatMessage({
              defaultMessage: 'COMPANY REGISTRATION NUMBER',
              id: 'h4mKm7',
              description: 'COMPANY REGISTRATION NUMBER field title',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: '01234567',
              id: 'IgDgSi',
              description: 'Company registration number field placeholder',
            })}
          />
        </>
      )}
    </>
  );
};

export { GbPaymentType };
